import type { EditorReadyFn, EditorSDK, GetAppManifest } from '@wix/platform-editor-sdk';

import { MembersAreaContext } from '../types/context';
import {
  initExperiments,
  initV3Experiments,
  shouldEnableMigrationToV3,
  shouldMigrateDisconnectedLoginBars,
} from '../utils/experiments';
import { initializeMonitoring } from '../utils/monitoring';
import {
  currentMembersArea,
  exportsFn,
  getControllerPresets,
  membersAreaOnMSB,
  splitMembersArea,
} from './platform-app';
import type { MembersAreaOnEventFn, MembersAreaPlatformApp } from './platform-app/types';
import { maybeFixDisconnectedLoginBars } from './data-fixers';
import * as applicationState from './services/applicationState';
import { getMembersAreaContext } from './services/context';
import { migrateToV3OnTemplates } from './services/migrations/v3/v3-migration';
import { parseStaticsUrlFromEditorScriptUrl } from './services/urls';

declare const self: {
  commonConfig?: { host?: string };
};

const editorPlatformAppMap: { [key in MembersAreaContext]: MembersAreaPlatformApp } = {
  [MembersAreaContext.V1]: currentMembersArea,
  [MembersAreaContext.V2]: membersAreaOnMSB,
  [MembersAreaContext.V3]: splitMembersArea,
};

const getEditorPlatformApp = async (editorSDK: EditorSDK) => {
  const membersAreaContext = await getMembersAreaContext(editorSDK);
  return editorPlatformAppMap[membersAreaContext];
};

const editorReady: EditorReadyFn = async (editorSDK, _appToken, options) => {
  const [appInstance, isBuilderEditor] = await Promise.all([
    editorSDK.info.getAppInstancePayload(''),
    editorSDK.info.isBuilderComponentModelSupported(''),
  ]);

  applicationState.setEditorSDK(editorSDK);
  applicationState.setStaticsBaseUrl(parseStaticsUrlFromEditorScriptUrl(options.initialAppData.editorScriptUrl));
  // @ts-expect-error - blogWriterProfilesOnly does not exist on Origin.info type in Editor platform sdk types
  applicationState.setIsBlogWriterProfilesOnly(!!options.origin.info?.blogWriterProfilesOnly);
  applicationState.setIsClassicEditor(options.origin.type === 'CLASSIC');
  applicationState.setIsResponsiveEditor(options.origin.type === 'RESPONSIVE');
  applicationState.setIsBuilderEditor(isBuilderEditor);
  applicationState.setIsStudio2Editor(isBuilderEditor && self?.commonConfig?.host === 'STUDIO2');

  initExperiments(appInstance);
  initV3Experiments(appInstance);

  try {
    await initializeMonitoring(editorSDK, options);
  } catch (e) {}

  const membersAreaContext = await getMembersAreaContext(editorSDK);
  const isV2 = membersAreaContext === MembersAreaContext.V2;

  if (isV2 && (await shouldEnableMigrationToV3())) {
    await migrateToV3OnTemplates(editorSDK);
  }

  if (await shouldMigrateDisconnectedLoginBars()) {
    await maybeFixDisconnectedLoginBars(editorSDK);
  }

  const platformApp = await getEditorPlatformApp(editorSDK);
  return platformApp.editorReady(editorSDK, _appToken, options);
};

const onEvent: MembersAreaOnEventFn = async (event, editorSDK) => {
  const platformApp = await getEditorPlatformApp(editorSDK);
  return platformApp.onEvent(event, editorSDK);
};

const getAppManifest: GetAppManifest = async (options, editorSDK, contextParams) => {
  const platformApp = await getEditorPlatformApp(editorSDK);
  return platformApp.getAppManifest(options, editorSDK, contextParams);
};

export { editorReady, exportsFn as exports, getAppManifest, getControllerPresets, onEvent };
